import type * as hast from 'hast';
import flatMap from 'unist-util-flatmap';

import { isFootnotesDefinitionSection } from './parseFootnotes';

// HTML elements that are not supported by the parsing
// and where we only want to keep the children.
const PASSTHROUGH_ELEMENTS = new Set([
    // Top level elements
    'html',
    'body',
    'main',
    'aside',
    'nav',
    'header',
    'article',

    // Inline elements
    /**
     * Google Docs uses <b> to wrap the copied content in the clipboard.
     * <b> shouldn't be used for styling either way https://developer.mozilla.org/en-US/docs/Web/HTML/Element/b
     *
     * For these reason it's a good compromise to convert it to a block
     * and retain all the content formatting from Google Docs (copy/paste)
     */
    'b',
]);

const SKIP_ELEMENTS = new Set(['head', 'style']);

/**
 * This transformer receives a hast and transforms it into a new hast.
 * It normalizes the tree by:
 * - Remove all comments
 * - Ignore top elements (head / body / html)
 * - Merging various footnotes definition sections
 */
export function normalizeHtmlTree() {
    return (tree: hast.Element | hast.Text | hast.Root | hast.Comment) => {
        let rootFootnotesDefinitionSection: hast.Element | undefined;
        const resultTree = flatMap(tree, (node) => {
            // Skip comments or elements that should be ignored
            if (node.type === 'comment' || SKIP_ELEMENTS.has(node.tagName)) {
                return [];
            }

            // Ignore text elements
            if (node.type === 'text') {
                return [node];
            }

            if (PASSTHROUGH_ELEMENTS.has(node.tagName)) {
                return node.children;
            }

            /**
             * As the markdown is parsed to an html tree, the various footnotes definitions are parsed
             * as separate sections. We want to merge them into a single section
             */
            if (isFootnotesDefinitionSection(node)) {
                if (!rootFootnotesDefinitionSection) {
                    rootFootnotesDefinitionSection = node;
                } else {
                    // Merge the footnotes sections with the root one
                    const rootFootnotesDefinitionSectionOL =
                        rootFootnotesDefinitionSection.children.find(
                            (child) => child.type === 'element' && child.tagName === 'ol'
                        );

                    const currentFootnotesDefinitionSectionOL: hast.Element = node.children.find(
                        (child) => child.type === 'element' && child.tagName === 'ol'
                    );

                    if (
                        rootFootnotesDefinitionSectionOL &&
                        'children' in rootFootnotesDefinitionSectionOL &&
                        currentFootnotesDefinitionSectionOL &&
                        'children' in currentFootnotesDefinitionSectionOL
                    ) {
                        rootFootnotesDefinitionSectionOL.children.push(
                            ...currentFootnotesDefinitionSectionOL.children
                        );
                    }

                    return [];
                }
            }

            return [node];
        });

        return resultTree;
    };
}
